import { enumMembers, type ApiSchema } from "../../apiSchema";
import { decodeSourceScope, readRecordKind } from "./sourceScopeDecoding";
import {
  JsonContractError,
  readArray,
  readBoolean,
  readInteger,
  readObject,
  readOneOf,
  readString,
} from "../../http/jsonDecoding";

/**
 * Selectors and placeholder sources of step definitions (processes.md P-6, W-2), checked
 * member by member as the server's contract states them. Shared by the step decoders and
 * the step form, which edits selectors of every kind.
 */

export type Selector = ApiSchema<"Selector">;
export type SelectorKind = Selector["kind"];
export type ItemSelector = ApiSchema<"ItemSelector">;
export type PlaceholderSource = ApiSchema<"PlaceholderSource">;
export type ValueKind = ApiSchema<"ValueKind">;
export type InputSlot = ApiSchema<"InputSlot">;

export const SELECTOR_KINDS = enumMembers<SelectorKind>({
  earlier_results: true,
  accepted_instance_data: true,
  reference_vocabularies: true,
  derived_records: true,
  corpus_documents: true,
  subject_text: true,
  label_list: true,
  ontology_terms: true,
  competency_questions: true,
  item: true,
  few_shot_examples: true,
  source_scope: true,
  source_version: true,
});

export const VALUE_KINDS = enumMembers<ValueKind>({
  text: true,
  choice: true,
  annotations: true,
  instance_data: true,
  change_set: true,
  text_units: true,
  item: true,
  item_refs: true,
  term_cards: true,
  check_report: true,
  derived: true,
  workspace_file: true,
  corpus_import: true,
  source_version: true,
  release_request: true,
  release: true,
  html_page: true,
  derived_records: true,
  memory: true,
  result: true,
});

const SUBJECT_RENDERINGS = enumMembers<ApiSchema<"SubjectRendering">>({ plain: true, numbered_segments: true });
const EXAMPLE_INCLUSIONS = enumMembers<ApiSchema<"ExampleInclusion">>({ all: true, matching_subject: true, none: true });
const TERM_KINDS = enumMembers<ApiSchema<"OntologyTermKind">>({
  class: true,
  object_property: true,
  datatype_property: true,
  annotation_property: true,
  individual: true,
});
const TERM_FILTERS = enumMembers<ApiSchema<"OntologyTermFilter">>({ all: true, labels_matching_subject: true });
const TERM_RENDERINGS = enumMembers<ApiSchema<"OntologyTermRendering">>({ term_cards: true, turtle: true });
type ExampleSource = ApiSchema<"FewShotExamplesSelector">["examples"];
const EXAMPLE_SOURCES = enumMembers<ExampleSource["source"]>({ ranking: true, tags: true, items: true });

export function decodeItemSelector(value: unknown, context: string): ItemSelector {
  const selector = readObject(value, context);
  if (typeof selector["item"] === "string") {
    return { item: selector["item"] };
  }
  // A role the project's bindings fill (process-governance.md PG-5).
  if (typeof selector["role"] === "string") {
    return { role: selector["role"] };
  }
  return { version: readString(selector, "version") };
}

export function decodeSelector(value: unknown): Selector {
  const selector = readObject(value, "selector");
  const kind = readOneOf(selector, "kind", SELECTOR_KINDS);
  const version = readInteger(selector, "version");
  switch (kind) {
    case "source_version": {
      if (version !== 1) throw new JsonContractError("Unknown source_version version.");
      const paths = selector["paths"] === undefined ? [] : readStrings(selector["paths"], "source paths");
      if (paths.length > 100 || paths.some((path) => path.length > 1000 || /[\\:]/.test(path) || Array.from(path).some((character) => character.charCodeAt(0) < 32) || path.split("/").some((segment) => segment === "" || segment === "." || segment === ".."))) {
        throw new JsonContractError("Expected at most 100 plain relative source paths, each at most 1000 characters.");
      }
      return { kind, version, paths };
    }
    case "earlier_results":
      if (version !== 1) throw new JsonContractError("Unknown earlier_results version.");
      return { kind, version };
    case "accepted_instance_data":
      if (version !== 1 && version !== 2) throw new JsonContractError("Unknown accepted_instance_data version.");
      return { kind, version };
    case "reference_vocabularies":
      if (version !== 1) throw new JsonContractError("Unknown reference_vocabularies version.");
      return { kind, version };
    case "derived_records":
      if (version !== 1) throw new JsonContractError("Unknown derived_records version.");
      return { kind, version, record_kind: readRecordKind(selector, "record_kind"), documents_of: readString(selector, "documents_of") };
    case "corpus_documents":
      if (version !== 1) throw new JsonContractError("Unknown corpus_documents version.");
      return {
        kind,
        version,
        without_records: selector["without_records"] == null ? null : readRecordKind(selector, "without_records"),
        segments: selector["segments"] == null ? null : readStrings(selector["segments"], "segments"),
      };
    case "source_scope":
      if (version !== 1) throw new JsonContractError("Unknown source_scope version.");
      return { kind, version, scope: decodeSourceScope(selector["scope"]) };
    case "subject_text":
      return { kind, version, scope: decodeScope(selector["scope"]), render: readOneOf(selector, "render", SUBJECT_RENDERINGS) };
    case "label_list":
      return {
        kind,
        version,
        label_list: decodeItemSelector(selector["label_list"], "label list"),
        include_guidelines: readBoolean(selector, "include_guidelines"),
        include_examples: readOneOf(selector, "include_examples", EXAMPLE_INCLUSIONS),
      };
    case "ontology_terms":
      return {
        kind,
        version,
        term_kinds: readArray(selector["term_kinds"], "term kinds", (term) => oneOf(term, TERM_KINDS, "term kind")),
        modules: readArray(selector["modules"], "modules", (module) => decodeItemSelector(module, "module")),
        filter: readOneOf(selector, "filter", TERM_FILTERS),
        render: readOneOf(selector, "render", TERM_RENDERINGS),
        annotation_properties: selector["annotation_properties"] === undefined ? [] : readStrings(selector["annotation_properties"], "annotation properties"),
        ...(selector["annotation_properties_from"] == null
          ? {}
          : { annotation_properties_from: decodeItemSelector(selector["annotation_properties_from"], "annotation properties artifact") }),
      };
    case "competency_questions":
      return { kind, version, tags: readStrings(selector["tags"], "tags"), include_sparql: readBoolean(selector, "include_sparql") };
    case "item":
      return { kind, version, item: decodeItemSelector(selector["item"], "item") };
    case "few_shot_examples":
      return {
        kind,
        version,
        examples: decodeExampleSource(selector["examples"]),
        maximum_examples: readInteger(selector, "maximum_examples"),
        include_notes: readBoolean(selector, "include_notes"),
      };
  }
}

/** Where few-shot examples come from: a ranking input, tags or pinned items (V-7). */
function decodeExampleSource(value: unknown): ExampleSource {
  const examples = readObject(value, "examples");
  const source = readOneOf(examples, "source", EXAMPLE_SOURCES);
  switch (source) {
    case "ranking":
      return { source, input: readString(examples, "input") };
    case "tags":
      return { source, tags: readStrings(examples["tags"], "tags") };
    case "items":
      return { source, items: readArray(examples["items"], "items", (item) => decodeItemSelector(item, "example")) };
  }
}

function decodeScope(value: unknown): ApiSchema<"SubjectTextSelector">["scope"] {
  if (value === "document" || value === "selection") {
    return value;
  }
  return { segment_type: readString(readObject(value, "scope"), "segment_type") };
}

export function decodePlaceholderSource(value: unknown): PlaceholderSource {
  const source = readObject(value, "source");
  if (source["source"] === "input") {
    return { source: "input", input: readString(source, "input") };
  }
  if (source["source"] === "selector") {
    return { source: "selector", selector: decodeSelector(source["selector"]) };
  }
  throw new JsonContractError('Expected "source" to be "selector" or "input".');
}

export function decodeInputSlot(value: unknown): InputSlot {
  const slot = readObject(value, "input slot");
  return { accepts: readArray(slot["accepts"], "accepts", (kind) => oneOf(kind, VALUE_KINDS, "value kind")), required: readBoolean(slot, "required") };
}

/** A JSON object whose members are each decoded by `decode`, such as `placeholders`. */
export function decodeRecord<T>(value: unknown, context: string, decode: (member: unknown) => T): Record<string, T> {
  if (value === undefined) {
    return {};
  }
  const record = readObject(value, context);
  return Object.fromEntries(Object.entries(record).map(([name, member]) => [name, decode(member)]));
}

export function readStrings(value: unknown, context: string): string[] {
  return readArray(value, context, (item) => {
    if (typeof item !== "string") {
      throw new JsonContractError(`Expected every member of ${context} to be a string.`);
    }
    return item;
  });
}

/** A bare string that must be one of `allowed`, such as an array member. */
export function oneOf<const T extends string>(value: unknown, allowed: readonly T[], context: string): T {
  const match = allowed.find((candidate) => candidate === value);
  if (match === undefined) {
    throw new JsonContractError(`Expected ${context} to be one of ${allowed.join(", ")}.`);
  }
  return match;
}

export function isSelectorSource(source: PlaceholderSource): source is ApiSchema<"SelectorSource"> {
  return source.source === "selector";
}
